<template>
  <section class="height-100-p" id="shipper-cutoff-groups-search-summary-section">
    <el-form
      :model="editForm"
      :rules="formRules"
      ref="editForm"
      size="mini"
    >
      <el-table
        :data="shipperCutoffGroup.list"
        size="mini"
        tooltip-effect="dark"
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        stripe
        v-loading="shipperCutoffGroup.loadingSearch"
        sortable
        @sort-change="handleSortChange"
        :default-sort="{prop: 'name', order: 'ascending'}"
      >
        <el-table-column
          prop="name"
          :label="$t('shipperCutoffGroup.name')"
          :min-width="2"
          align="left"
          header-align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <el-form-item
              v-if="$can(permissions.WAREHOUSE.EDIT_SHIPPER_CUTOFF_GROUP)"
              v-show="scope.row.edit"
              prop="name"
            >
              <el-input
                v-model="editForm.name"
                v-alphanumeric-validation
                maxlength="35"
                clearable
                class="inline-input"
                id="form-group-name"
                ref="formGroupName"
              ></el-input>
            </el-form-item>
            <span v-show="!scope.row.edit">{{ scope.row.name }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="numberOfShippers"
          :label="$t('shipperCutoffGroup.numberOfShippers')"
          :min-width="1"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <span class="blue-link-bold" @click="openShippersTab(scope.row.id, scope.row.name)"> {{ scope.row.numberOfShippers }} </span>
          </template>
        </el-table-column>
        <el-table-column
          :min-width="1"
          :label="$t('common.options')"
          align="center"
          class-name="fixed-width"
        >
          <template slot-scope="scope">
            <span v-show="!scope.row.edit && !shipperCutoffGroup.isEditing">
              <cutoff-group-detail-dialog :shipper-cutoff-group-id="scope.row.id" :name="scope.row.name" :number-of-shippers="scope.row.numberOfShippers" />
              <el-tooltip
                v-if="$can(permissions.WAREHOUSE.EDIT_SHIPPER_CUTOFF_GROUP)"
                class="item"
                effect="dark"
                :content="$t('common.edit')"
                placement="top-start"
              >
                <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)" />
              </el-tooltip>
              <el-tooltip
                v-if="$can(permissions.WAREHOUSE.DELETE_SHIPPER_CUTOFF_GROUP)"
                class="item"
                effect="dark"
                :content="$t('common.delete')"
                placement="top-start"
              >
                <i class="el-icon-delete icon-action" @click="handleDelete(scope.row)"></i>
              </el-tooltip>
            </span>
            <el-button v-show="scope.row.edit" icon="el-icon-success" circle size="mini" @click="handleUpdate(scope.row)" />
            <el-button v-show="scope.row.edit" icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)" />
          </template>
        </el-table-column>
      </el-table>
    </el-form>

    <div class="pagination-footer" v-show="shipperCutoffGroup.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('shipperCutoffGroup.totalGroups') }}</span>
           {{ shipperCutoffGroup.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="shipperCutoffGroup.actualFilters.rows"
        :current-page.sync="shipperCutoffGroup.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="shipperCutoffGroup.total"
        layout="sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { Message } from 'element-ui';
import {
  SHIPPER_CUTOFF_GROUP_SEARCH, SHIPPER_CUTOFF_GROUP_DELETE, SHIPPER_CUTOFF_GROUP_SET_EDITING, SHIPPER_CUTOFF_GROUP_EDIT
} from '@/store/actions';
import constants from '@/utils/constants';
import CutoffGroupDetailDialog from './components/CutoffGroupDetailDialog';

export default {
  name: 'ShipperCutoffGroupsSummarySection',
  components: {
    CutoffGroupDetailDialog
  },
  computed: {
    ...mapGetters([
      'shipperCutoffGroup'
    ]),
    sizes() {
      return constants.TABLES.DEFAULT_LIMIT_SIZES;
    }
  },
  data() {
    return {
      editForm: {
        id: null,
        name: null,
      },
      formRules: {
        name: [
          {
            required: true,
            message: this.$t('shipperCutoffGroup.cutoffGroupNameRequired'),
            trigger: 'blur'
          }
        ]
      },
    };
  },
  methods: {
    handleSearchChangeLimit(val) {
      this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SEARCH, { rows: val });
    },
    handleSearchChangePage(val) {
      this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SEARCH, { page: val });
    },
    handleSortChange({ prop, order }) {
      this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SEARCH, {
        orderField: prop,
        orderDirection: constants.TABLES.ORDER_DIRECTION[order]
      });
    },
    openShippersTab(shipperCutoffGroupId, shipperCutoffGroupName) {
      const routeData = this.$router.resolve({
        path: '/warehouse/manageShippers',
        query: {
          shipperCutoffGroupId,
          shipperCutoffGroupName,
        }
      });
      window.open(routeData.href, '_blank');
    },
    async handleDelete(row) {
      Message.closeAll();
      if (row.numberOfShippers && row.numberOfShippers > 0) {
        this.$message.error(this.$t('shipperCutoffGroup.error.numberOfShippersInvalid'));
        return;
      }

      const confirm = await this.$confirm(
        this.$t('shipperCutoffGroup.confirmDelete'), { confirmButtonText: this.$t('common.yes') }
      ).catch(() => false);

      if (confirm) {
        const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
        this.$store.dispatch(SHIPPER_CUTOFF_GROUP_DELETE, row.id)
          .then((response) => {
            this.$message.success(response.message);
            this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SEARCH, {});
          })
          .finally(() => loading.close());
      }
    },
    handleEdit(row) {
      this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SET_EDITING, true);
      row.edit = true;
      this.editForm.id = row.id;
      this.editForm.name = row.name;
    },
    cancelEdit(row) {
      this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SET_EDITING, false);
      row.edit = false;
    },
    handleUpdate(row) {
      this.$refs.editForm.validate(async (valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.$store.dispatch(SHIPPER_CUTOFF_GROUP_EDIT, this.editForm)
            .then((response) => {
              this.cancelEdit(row);
              this.$message.success(response.message);
              this.$store.dispatch(SHIPPER_CUTOFF_GROUP_SEARCH, {});
            })
            .finally(() => {
              loading.close();
            });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}`;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    }
  },
};
</script>
